/**
 * API layer of the AI assistant chat on the order/offer detail page.
 * Mirrors entities/chat_inbox.js: plain reqres handlers returning Deferreds,
 * URLs on the compuzz2 backend (/compuzz/rest/v2/ai-chat).
 */
define('entities/ai_chat',[
  'app',
  'jquery',
  'settings'
], function (App, $, Settings) {
  'use strict';

  var API = {
    openSessionUrl: function (poi) { return Settings.url('compuzz2', 'v2/ai-chat/sessions/order/' + poi); },
    openAttachmentSessionUrl: function (poi, fileUid) {
      return Settings.url('compuzz2', 'v2/ai-chat/sessions/attachment/' + poi + '/' + fileUid);
    },
    messagesUrl: function (secId, sinceId) {
      var params = {};
      if (sinceId) { params.sinceId = sinceId; }
      return Settings.url('compuzz2', 'v2/ai-chat/sessions/' + secId + '/messages', params);
    },
    sendUrl:   function (secId) { return Settings.url('compuzz2', 'v2/ai-chat/sessions/' + secId + '/messages'); },
    uploadUrl: function (secId) { return Settings.url('compuzz2', 'v2/ai-chat/sessions/' + secId + '/upload'); },
    contextUrl: function (secId, messageSecId, action) {
      return Settings.url('compuzz2', 'v2/ai-chat/sessions/' + secId + '/messages/' + messageSecId + '/' + action);
    },
    jobsUrl:   function (secId) { return Settings.url('compuzz2', 'v2/ai-chat/sessions/' + secId + '/jobs'); },
    modelsUrl: function ()      { return Settings.url('compuzz2', 'v2/ai-chat/models'); },
    accessUrl: function ()      { return Settings.url('compuzz2', 'v2/ai-chat/access'); },
    applyUrl:  function (secId) { return Settings.url('compuzz2', 'v2/ai-chat/edit-jobs/' + secId + '/apply'); },
    rejectUrl: function (secId) { return Settings.url('compuzz2', 'v2/ai-chat/edit-jobs/' + secId + '/reject'); },
    actionJobsUrl:   function (secId) { return Settings.url('compuzz2', 'v2/ai-chat/sessions/' + secId + '/action-jobs'); },
    actionApplyUrl:  function (secId) { return Settings.url('compuzz2', 'v2/ai-chat/action-jobs/' + secId + '/apply'); },
    actionRejectUrl: function (secId) { return Settings.url('compuzz2', 'v2/ai-chat/action-jobs/' + secId + '/reject'); },
    configUrl: function ()      { return Settings.url('compuzz2', 'v2/ai-chat/config'); }
  };

  // Opens (or resumes) the order's AI session. Resolves with the session
  // json including the full message history and proposal cards.
  App.reqres.setHandler('aiChat:open-session', function (poi) {
    var defer = $.Deferred();
    $.ajax({
      type: 'POST',
      url: API.openSessionUrl(poi),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  // Opens (or resumes) the file-editing chat scoped to ONE attachment of the
  // order (or its customer).
  App.reqres.setHandler('aiChat:open-attachment-session', function (poi, fileUid) {
    var defer = $.Deferred();
    $.ajax({
      type: 'POST',
      url: API.openAttachmentSessionUrl(poi, fileUid),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  // Poll fallback; sinceId is the rowId of the last message already shown.
  // Hard timeout: the poll loop must never be killed by one hung request.
  App.reqres.setHandler('aiChat:messages', function (secId, sinceId) {
    var defer = $.Deferred();
    $.ajax({
      type: 'GET',
      url: API.messagesUrl(secId, sinceId),
      timeout: 20000,
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  // Whether the CALLING USER may use the assistant (account role +
  // AI_ASSISTANT user-group membership). Resolves false on any error so the
  // tab simply stays hidden.
  App.reqres.setHandler('aiChat:access', function () {
    var defer = $.Deferred();
    $.ajax({
      type: 'GET',
      url: API.accessUrl(),
      success: function (data) { defer.resolve(!!(data && data.enabled === true)); },
      error: function () { defer.resolve(false); }
    });
    return defer.promise();
  });

  // The account's selectable AI models (code + label, no secrets).
  App.reqres.setHandler('aiChat:models', function () {
    var defer = $.Deferred();
    $.ajax({
      type: 'GET',
      url: API.modelsUrl(),
      success: function (data) { defer.resolve(data || []); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  // 202 = accepted, the answer arrives async. 409 = a turn is already
  // running — surfaced as {busy: true} so the view shows the thinking state.
  // modelCode (optional) switches the session to that model from this
  // message on; omit to keep the session's current selection.
  App.reqres.setHandler('aiChat:send-message', function (secId, text, modelCode) {
    var defer = $.Deferred();
    $.ajax({
      type: 'POST',
      url: API.sendUrl(secId),
      contentType: 'application/json',
      data: JSON.stringify({ text: text, modelCode: modelCode || null }),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) {
        if (xhr && xhr.status === 409) {
          defer.reject({ busy: true });
        } else {
          defer.reject(xhr);
        }
      }
    });
    return defer.promise();
  });

  // Flip a message's exchange out of / back into the AI context. Resolves
  // with the full session json (the thread re-renders from it).
  App.reqres.setHandler('aiChat:set-message-context', function (secId, messageSecId, included) {
    var defer = $.Deferred();
    $.ajax({
      type: 'POST',
      url: API.contextUrl(secId, messageSecId, included ? 'include' : 'exclude'),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  // Multipart upload of one chat file; resolves with the created message
  // json (fileUrl/fileName filled). 415 = extension not accepted.
  App.reqres.setHandler('aiChat:upload-file', function (secId, file) {
    var defer = $.Deferred();
    var formData = new FormData();
    formData.append('file', file);
    $.ajax({
      type: 'POST',
      url: API.uploadUrl(secId),
      data: formData,
      processData: false,
      contentType: false,
      success: function (data) { defer.resolve(data); },
      error: function (xhr) {
        if (xhr && xhr.status === 415) {
          defer.reject({ unsupported: true });
        } else {
          defer.reject(xhr);
        }
      }
    });
    return defer.promise();
  });

  App.reqres.setHandler('aiChat:jobs', function (secId) {
    var defer = $.Deferred();
    $.ajax({
      type: 'GET',
      url: API.jobsUrl(secId),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  // Apply resolves with the updated job json; a 409 means the proposal is
  // stale (order validated, image changed, already decided) — reject with
  // {conflict: true} so the card can refresh instead of showing a raw error.
  App.reqres.setHandler('aiChat:apply-edit', function (jobSecId) {
    var defer = $.Deferred();
    $.ajax({
      type: 'POST',
      url: API.applyUrl(jobSecId),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) {
        if (xhr && xhr.status === 409) {
          defer.reject({ conflict: true, message: xhr.responseJSON ? xhr.responseJSON.message : null });
        } else {
          defer.reject(xhr);
        }
      }
    });
    return defer.promise();
  });

  App.reqres.setHandler('aiChat:reject-edit', function (jobSecId) {
    var defer = $.Deferred();
    $.ajax({
      type: 'POST',
      url: API.rejectUrl(jobSecId),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  // Mutating API-call proposals (generic call_api tool) — the action twin of
  // the edit jobs above. Same 409 = conflict convention on apply.
  App.reqres.setHandler('aiChat:action-jobs', function (secId) {
    var defer = $.Deferred();
    $.ajax({
      type: 'GET',
      url: API.actionJobsUrl(secId),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  App.reqres.setHandler('aiChat:apply-action', function (jobSecId) {
    var defer = $.Deferred();
    $.ajax({
      type: 'POST',
      url: API.actionApplyUrl(jobSecId),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) {
        if (xhr && xhr.status === 409) {
          defer.reject({ conflict: true, message: xhr.responseJSON ? xhr.responseJSON.message : null });
        } else {
          defer.reject(xhr);
        }
      }
    });
    return defer.promise();
  });

  App.reqres.setHandler('aiChat:reject-action', function (jobSecId) {
    var defer = $.Deferred();
    $.ajax({
      type: 'POST',
      url: API.actionRejectUrl(jobSecId),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  App.reqres.setHandler('aiChat:get-config', function () {
    var defer = $.Deferred();
    $.ajax({
      type: 'GET',
      url: API.configUrl(),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  App.reqres.setHandler('aiChat:update-config', function (config) {
    var defer = $.Deferred();
    $.ajax({
      type: 'PUT',
      url: API.configUrl(),
      contentType: 'application/json',
      data: JSON.stringify(config),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });
});

